import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Typography,
} from '@mui/material';
import ArchiveIcon from '@mui/icons-material/Archive';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import PersonIcon from '@mui/icons-material/Person';
import RestoreIcon from '@mui/icons-material/Restore';
import { computeDaysRemaining, formatArchiveDate } from '../../Helpers/archiveUtils';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';

/**
 * Card for an archived project. Displays archive date, days-remaining countdown,
 * and role-gated Restore / Delete buttons.
 *
 * Props:
 *   project        {Object}   - project data including archived_on
 *   effectiveRole  {number}   - 0=SuperUser, 1=Admin, 2=Contributor, 3=Observer
 *   onRestore      {Function} - called with (project) when restore is confirmed
 *   onDelete       {Function} - called with (project) when Delete is clicked (admin+ only)
 */
const ArchivedProjectCard = ({ project, effectiveRole, onRestore, onDelete }) => {
  const isPrivileged = effectiveRole <= 1;   // Admin or Super User
  const canRestore = effectiveRole <= 2;      // Contributor, Admin, Super User
  const daysRemaining = computeDaysRemaining(project.archived_on);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);

  const handleRestoreClick = () => setRestoreDialogOpen(true);
  const handleRestoreConfirmed = () => {
    setRestoreDialogOpen(false);
    onRestore(project);
  };

  return (
    <>
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          opacity: 0.75,
          border: '1px dashed',
          borderColor: 'text.disabled',
          backgroundColor: 'action.hover',
          position: 'relative',
        }}
      >
      {/* ARCHIVED chip */}
      <Chip
        icon={<ArchiveIcon fontSize="small" />}
        label="ARCHIVED"
        size="small"
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 1,
          fontWeight: 600,
          backgroundColor: 'grey.300',
          color: 'text.secondary',
        }}
      />

      <CardContent sx={{ flexGrow: 1, pb: 0 }}>
        {/* Project title */}
        <Typography
          variant="h6"
          component="div"
          gutterBottom
          sx={{
            fontWeight: 600,
            pr: 10,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            color: 'text.secondary',
          }}
        >
          {project.title}
        </Typography>

        {/* Created by */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
          <PersonIcon fontSize="small" color="disabled" />
          <Typography variant="body2" color="text.disabled">
            {project.created_by}
          </Typography>
        </Box>

        {/* Archive date */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
          <CalendarTodayIcon fontSize="small" color="disabled" />
          <Typography variant="body2" color="text.disabled">
            {formatArchiveDate(project.archived_on)}
          </Typography>
        </Box>

        {/* Days remaining */}
        <Typography
          variant="caption"
          sx={{
            color: daysRemaining <= 3 ? 'error.main' : 'text.disabled',
            fontWeight: daysRemaining <= 3 ? 600 : 400,
          }}
        >
          {daysRemaining === 0 ? 'Deletes today' : `Deleted in ${daysRemaining} day(s)`}
        </Typography>
      </CardContent>

      {/* Action buttons */}
      <Box sx={{ display: 'flex', gap: 1, p: 1.5, pt: 1, justifyContent: 'flex-end' }}>
        {canRestore && (
          <Button
            size="small"
            variant="outlined"
            color="success"
            startIcon={<RestoreIcon />}
            onClick={handleRestoreClick}
          >
            Restore
          </Button>
        )}
        {isPrivileged && (
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<DeleteForeverIcon />}
            onClick={() => onDelete(project)}
          >
            Delete
          </Button>
        )}
      </Box>
    </Card>

      <DeleteConfirmationDialog
        open={restoreDialogOpen}
        onClose={() => setRestoreDialogOpen(false)}
        onRestore={handleRestoreConfirmed}
        project={project}
        mode="restore"
      />
    </>
  );
};

export default ArchivedProjectCard;
